import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineLightBulb,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineCloudArrowUp,
  HiOutlinePrinter,
  HiOutlineTableCells,
} from "react-icons/hi2";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  authService,
  kaizenService,
  masterDataService,
  machineService,
  plantService,
  productService,
  userService,
  uploadService,
} from "../services";
import axios from "axios";
import { usePermissions } from "../hooks/usePermissions";
import KaizenResults from "../components/KaizenResults";
import { API_URL } from "../config/api";
import PageLoading from "../components/common/PageLoading";
import "./KaizenEntry.css";

const getImageUrl = (path) => {
  if (!path) return "";
  if (
    path.startsWith("http") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  )
    return path;
  // Determine separator
  const separator = path.startsWith("/") ? "" : "/";
  return `${API_URL}${separator}${path}`;
};

const KaizenEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();
  const { hasPermission } = usePermissions();
  const isEditMode = !!id;

  // Permission checks
  const canCreate = hasPermission("Kaizen.Create");
  const canEdit = hasPermission("Kaizen.Edit");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dropdown data
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    problemStatement: "",
    problemStatementImage: "",
    proposedIdeaImage: "",
    currentState: "",
    proposedIdea: "",
    plantId: "",
    categoryId: "",
    estimatedSavings: "",
    machineId: "",
    productId: "",
    statusId: "",
    referenceNumber: null,
    moduleName: "",
    benchmark: "",
    kaizenStartDate: "",
    kaizenFinishDate: "",
    teamMembers: [], // Array of user IDs
    kaizenCost: "",
    benefits: "",
    resultArea: [], // Array of selected Result Areas (P, Q, C, D, S, M)
    activity: "",
    lossNumber: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const location = useLocation();

  useEffect(() => {
    if (tenantId) {
      if (isEditMode && !canEdit) {
        setError("You do not have permission to edit Kaizen ideas");
        setLoading(false);
        return;
      }
      if (!isEditMode && !canCreate) {
        setError("You do not have permission to create Kaizen ideas");
        setLoading(false);
        return;
      }

      // Load cached data immediately if available
      const cachedCategories = sessionStorage.getItem("kaizen_categories");
      const cachedMachines = sessionStorage.getItem("machines_data");
      const cachedProducts = sessionStorage.getItem("products_data");
      const cachedStatuses = sessionStorage.getItem("kaizen_statuses");

      if (cachedCategories) {
        setCategories(JSON.parse(cachedCategories));
      }
      if (cachedMachines) {
        setMachines(JSON.parse(cachedMachines));
      }
      if (cachedProducts) {
        setProducts(JSON.parse(cachedProducts));
      }
      if (cachedStatuses) {
        setStatuses(JSON.parse(cachedStatuses));
      }

      // Load fresh data in background
      loadInitialData();

      // Check navigation state for pre-filling data (only if not editing existing)
      if (!isEditMode && location.state) {
        const {
          title,
          problemStatement,
          currentState,
          proposedIdea,
          machineId,
          plantId,
        } = location.state;
        setFormData((prev) => ({
          ...prev,
          title: title || prev.title,
          problemStatement: problemStatement || prev.problemStatement,
          currentState: currentState || prev.currentState,
          proposedIdea: proposedIdea || prev.proposedIdea,
          plantId: plantId || prev.plantId,
          machineId: machineId || prev.machineId,
          referenceNumber:
            location.state.referenceNumber || prev.referenceNumber,
          moduleName: location.state.moduleName || prev.moduleName,
          resultArea: location.state.resultArea || [],
          activity: location.state.activity || "",
          lossNumber: location.state.lossNumber || "",
        }));
      }
    } else {
      setError("Please login to enter Kaizen idea");
      setLoading(false);
    }
  }, [tenantId, id, isEditMode, canCreate, canEdit, location.state]);

  // Handle auto-print from navigation
  useEffect(() => {
    if (!loading && location.state?.autoPrint) {
      // Small delay to ensure rendering is complete
      const timer = setTimeout(() => {
        window.print();
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (!loading && location.state?.autoExport) {
      const timer = setTimeout(() => {
        handleExportExcel();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, location.state]);

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Kaizen Idea Sheet", {
      views: [{ showGridLines: false }],
    });

    // Set Column Widths (A-O) - 15 Columns
    sheet.columns = [
      { width: 15 }, // A
      { width: 15 }, // B
      { width: 15 }, // C
      { width: 8 },  // D (Activity Label / P)
      { width: 5 },  // E (KK / Q)
      { width: 5 },  // F (JH / C)
      { width: 5 },  // G (PM / D)
      { width: 5 },  // H (ET / S)
      { width: 5 },  // I (MP / M)
      { width: 5 },  // J (QM)
      { width: 5 },  // K (OT)
      { width: 5 },  // L (SE)
      { width: 12 }, // M
      { width: 12 }, // N
      { width: 12 }, // O
    ];
    // Correcting indices:
    // A=1, B=2, C=3, D=4
    // E=5, F=6, G=7, H=8, I=9, J=10, K=11, L=12
    // M=13, N=14, O=15

    // Helper for Borders
    const thinBorder = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    const thickBorder = {
      top: { style: "medium" },
      left: { style: "medium" },
      bottom: { style: "medium" },
      right: { style: "medium" },
    };

    // --- ROW 1: HEADER & ACTIVITY CODES ---
    // A1:C2 -> Cyan Block
    // A1:C2 -> Cyan Block
    sheet.mergeCells("A1:C2");
    const cyanLeft = sheet.getCell("A1");
    // Left Header: Company Name (Dynamic)
    const tenantInfo = authService.getTenantInfo();
    cyanLeft.value = tenantInfo?.tenantName || "TPM CLUB INDIA";
    cyanLeft.font = { bold: true, size: 14 }; // Standard header style
    cyanLeft.alignment = { horizontal: "center", vertical: "middle" };
    cyanLeft.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF00FFFF" } }; // Cyan
    cyanLeft.border = thinBorder;

    // D1 -> Activity Label
    const actLabel = sheet.getCell("D1");
    actLabel.value = "Activity";
    actLabel.font = { italic: true, bold: true, color: { argb: "FF0000FF" } }; // Blue/Italic in image? Looks blueish
    actLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } }; // Grey
    actLabel.alignment = { horizontal: "center", vertical: "middle" };
    actLabel.border = thinBorder;

    // E1:L1 -> Activity Codes
    const actCodes = ["KK", "JH", "PM", "ET", "MP", "QM", "OT", "SE"];
    actCodes.forEach((code, idx) => {
      const cell = sheet.getRow(1).getCell(5 + idx); // E is 5
      cell.value = code;
      cell.font = { bold: true, color: { argb: "FF0000FF" } }; // Blue text
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = thinBorder;
      if (formData.activity === code) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF00" } }; // Yellow Highlight
      }
    });

    // M1:O2 -> Cyan Block Right
    sheet.mergeCells("M1:O2");
    const cyanRight = sheet.getCell("M1");
    // Right Header: Sheet Title
    cyanRight.value = "KAIZEN SHEET";
    cyanRight.font = { bold: true, size: 14 };
    cyanRight.alignment = { horizontal: "center", vertical: "middle" };
    cyanRight.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF00FFFF" } };
    cyanRight.border = thinBorder;

    // --- ROW 2: LOSS NO ---
    // D2 -> Loss Sr No Label
    const lossLabel = sheet.getCell("D2");
    lossLabel.value = "Loss Sr No";
    lossLabel.font = { italic: true, bold: true, color: { argb: "FF0000FF" } };
    lossLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };
    lossLabel.alignment = { horizontal: "center", vertical: "middle" };
    lossLabel.border = thinBorder;

    // E2:L2 -> Loss Number Values (Merged or individual?) Image shows blank boxes.
    // We'll merge them for the value.
    sheet.mergeCells("E2:L2");
    const lossValue = sheet.getCell("E2");
    lossValue.value = formData.lossNumber || "";
    lossValue.alignment = { horizontal: "center", vertical: "middle" };
    lossValue.border = thinBorder;


    // --- ROW 3: KAIZEN NO & RESULT AREA ---
    // A3 -> KAIZEN No.
    const kNoLabel = sheet.getCell("A3");
    kNoLabel.value = "KAIZEN No.";
    kNoLabel.font = { color: { argb: "FFFF0000" }, bold: true }; // Red
    kNoLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6FA" } }; // Light Purple/Blue
    kNoLabel.border = thinBorder;

    // B3 -> Value (Merged A-B? No, A is label, B is val)
    // Wait, layout A is wide. Let's merge B3 if needed. 
    // Actually image shows: Kaizen No [Box] | [Big Box for Result Area stuff?]
    // Let's stick to A3 Label, B3 Value.
    // C3: Result Area Label.
    const kNoVal = sheet.getCell("B3");
    kNoVal.value = formData.referenceNumber || "";
    kNoVal.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6FA" } };
    kNoVal.border = thinBorder;

    // C3 -> Result Area Label
    const resLabel = sheet.getCell("C3");
    resLabel.value = "Result Area";
    resLabel.font = { italic: true, bold: true, color: { argb: "FF0000FF" } };
    resLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };
    resLabel.alignment = { horizontal: "center", vertical: "middle" };
    resLabel.border = thinBorder;

    // D3:I3 -> Result Codes (P, Q, C, D, S, M)
    const resCodes = ["P", "Q", "C", "D", "S", "M"];
    resCodes.forEach((code, idx) => {
      const cell = sheet.getRow(3).getCell(4 + idx); // D is 4
      cell.value = code;
      cell.font = { bold: true, color: { argb: "FF0000FF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = thinBorder;
      if ((formData.resultArea || []).includes(code)) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF00" } };
      }
    });

    // J3:O3 -> Empty / Merged
    sheet.mergeCells("J3:O3");
    sheet.getCell("J3").border = thinBorder;


    // --- ROW 4: THEME / MACHINE / IDEA / TEAM ---
    // A4 -> "01) Kaizen - Theme:" (Label+Value area?)
    // Image shows A4 as blue header line? No, it looks like:
    // Row 4: [01 Kaizen Theme Header (A4-C4?)] [Machine Header D4] [Machine Val E4-F4?] [Idea Header G4] ...
    // Let's try to match the row 4 structure:

    // A4 -> Theme Label
    const themeLabel = sheet.getCell("A4");
    themeLabel.value = "01) Kaizen - Theme:";
    themeLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    themeLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6FA" } };
    themeLabel.border = thinBorder;
    sheet.mergeCells("A4:C4"); // Header spans A-C? Or content is below?
    // Image shows content is BELOW in a big blue box (Row 5-6).
    // So Row 4 is just the label line?

    // A5:C6 -> Theme Content (Blue Box)
    sheet.mergeCells("A5:C6");
    const themeContent = sheet.getCell("A5");
    themeContent.value = formData.title || "";
    themeContent.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDCE6F1" } }; // Light Blue
    themeContent.alignment = { vertical: "top", wrapText: true };
    themeContent.border = thinBorder;

    // D4 -> Machine Label
    const machLabel = sheet.getCell("D4");
    machLabel.value = "Machine :";
    machLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    machLabel.border = thinBorder;

    // E4:F4 -> Machine Value
    sheet.mergeCells("E4:F4");
    const machVal = sheet.getCell("E4");
    // Find machine name
    const mName = machines.find((m) => m.machineId == formData.machineId)?.machineName || formData.machineId;
    machVal.value = mName || "";
    machVal.alignment = { vertical: "middle", horizontal: "center" };
    machVal.border = thinBorder;

    // G4 -> Idea Label?
    // In image: "Idea :" is roughly in the middle.
    const ideaLabel = sheet.getCell("G4"); // G is start of Idea?
    ideaLabel.value = "Idea :";
    ideaLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    ideaLabel.border = thinBorder;

    // H4:I4 -> Idea Value? Or is Idea content below?
    // Image shows "Idea :" header, then blank space below for idea?
    // But there is also "Team" at J4.
    // Let's assume Idea title is here, or empty.
    sheet.mergeCells("H4:I4");
    sheet.getCell("H4").border = thinBorder;

    // J4 -> Team Label
    const teamLabel = sheet.getCell("J4");
    teamLabel.value = "Team";
    teamLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    teamLabel.border = thinBorder;

    // K4:O4 -> Team Value?
    // Wait, K-O is for Bench Mark in Row 5.
    // In Row 4, K-O is Team Members?
    sheet.mergeCells("K4:O4");
    const teamVal = sheet.getCell("K4");
    teamVal.value = (formData.teamMembers || []).join(", ");
    teamVal.border = thinBorder;


    // --- ROW 5 & 6 (Middle/Right Side) ---
    // D5:J6 -> Idea Description / Middle block
    sheet.mergeCells("D5:J6");
    const ideaDesc = sheet.getCell("D5");
    ideaDesc.value = formData.proposedIdea || ""; // Using proposed idea here?
    ideaDesc.alignment = { vertical: "top", wrapText: true };
    ideaDesc.border = thinBorder;

    // K5:O6 -> Bench Mark Table
    // K5: Bench Mark, L5: Target, M5: Start Date, N5: Finish Date?
    // Wait, we have 5 cols K-O. Image shows 4 columns.
    // Let's merge N:O for Finish Date?
    const setHeader = (cell, text) => {
      cell.value = text;
      cell.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
      cell.border = thinBorder;
      cell.alignment = { horizontal: "center" };
    };

    setHeader(sheet.getCell("K5"), "Bench Mark");
    setHeader(sheet.getCell("L5"), "Target");
    setHeader(sheet.getCell("M5"), "Start Date");
    sheet.mergeCells("N5:O5");
    setHeader(sheet.getCell("N5"), "Finish Date");

    const setVal = (cell, text) => {
      cell.value = text || "";
      cell.alignment = { horizontal: "center" };
      cell.border = thinBorder;
    };

    setVal(sheet.getCell("K6"), formData.benchmark);
    setVal(sheet.getCell("L6"), ""); // Target val
    setVal(sheet.getCell("M6"), formData.kaizenStartDate); // Date
    sheet.mergeCells("N6:O6");
    setVal(sheet.getCell("N6"), formData.kaizenFinishDate); // Date


    // --- ROW 7: HEADERS FOR PROBLEM, ROOT CAUSE, BENEFITS ---
    // A7 -> "02) Problem / Present status :"
    const probLabel = sheet.getCell("A7");
    probLabel.value = "02) Problem / Present status :";
    probLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("A7:C7");
    probLabel.border = thinBorder;

    // D7 -> "04) Root-cause:"
    const rootLabel = sheet.getCell("D7");
    rootLabel.value = "04) Root-cause:";
    rootLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("D7:J7");
    rootLabel.border = thinBorder;

    // K7 -> "07) Benefits :"
    const benLabel = sheet.getCell("K7");
    benLabel.value = "07) Benefits :";
    benLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("K7:O7");
    benLabel.border = thinBorder;


    // --- ROW 8-10: CONTENT FOR PROBLEM, ROOT CAUSE, BENEFITS ---
    // A8:C10 -> Problem Content
    sheet.mergeCells("A8:C10");
    const probTxt = sheet.getCell("A8");
    probTxt.value = formData.problemStatement || "";
    probTxt.alignment = { vertical: "top", wrapText: true };
    probTxt.border = thinBorder;

    // D8:J10 -> Root Cause Content (Use blank if no field)
    sheet.mergeCells("D8:J8"); // Row 8 only? No 8-? 
    // Image Row 9 has "05 Countermeasure". So Row 8 is Root Cause Content.
    // Row 9 is Countermeasure Header.
    // So Root Cause is just D8:J8.
    const rootTxt = sheet.getCell("D8");
    rootTxt.value = ""; // No field mapped
    rootTxt.border = thinBorder;

    // K8:O8 -> Benefits Content
    sheet.mergeCells("K8:O8");
    const benTxt = sheet.getCell("K8");
    benTxt.value = formData.benefits || "";
    benTxt.border = thinBorder;


    // --- ROW 9-10 (Middle/Right Split) ---
    // D9 -> "05) Countermeasure :"
    const countLabel = sheet.getCell("D9");
    countLabel.value = "05) Countermeasure :";
    countLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("D9:J9");
    countLabel.border = thinBorder;

    // D10:J10 -> Countermeasure Content
    sheet.mergeCells("D10:J10");
    const countTxt = sheet.getCell("D10");
    countTxt.value = formData.proposedIdea || "";
    countTxt.alignment = { vertical: "top", wrapText: true };
    countTxt.border = thinBorder;

    // K9 -> "08) Cost of the Kaizen :"
    const costLabel = sheet.getCell("K9");
    costLabel.value = "08) Cost of the Kaizen :";
    costLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("K9:O9");
    costLabel.border = thinBorder;

    // K10:O10 -> Cost Content
    sheet.mergeCells("K10:O10");
    const costTxt = sheet.getCell("K10");
    costTxt.value = formData.kaizenCost || "";
    costTxt.border = thinBorder;


    // --- ROW 11: BEFORE/AFTER HEADER ---
    // A11 -> "Before Kaizen" (Blue text, Italics?)
    // Note: Image says "Before Kaizen" at A11.
    const beforeLabel = sheet.getCell("A11");
    beforeLabel.value = "Before Kaizen";
    beforeLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("A11:C11");
    beforeLabel.border = thinBorder;

    // D11 -> "After Kaizen"
    const afterLabel = sheet.getCell("D11");
    afterLabel.value = "After Kaizen";
    afterLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("D11:J11");
    afterLabel.border = thinBorder;

    // K11 -> "09) Standardisation / How to sustain :"
    const stdLabel = sheet.getCell("K11");
    stdLabel.value = "09) Standardisation / How to sustain :";
    stdLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("K11:O11");
    stdLabel.border = thinBorder;


    // --- ROW 12-25: IMAGES & STANDARDISATION ---
    // A12:C25 -> Image 1 (Before)
    sheet.mergeCells("A12:C25");
    const img1Cell = sheet.getCell("A12");
    img1Cell.border = thinBorder;

    // D12:J25 -> Image 2 (After)
    sheet.mergeCells("D12:J25");
    const img2Cell = sheet.getCell("D12");
    img2Cell.border = thinBorder;

    // K12:O25 -> Standardisation Content (Blank box)
    sheet.mergeCells("K12:O25");
    const stdContent = sheet.getCell("K12");
    stdContent.border = thinBorder;


    // --- ROW 26: ANALYSIS & RESULTS ---
    // A26 -> "03) Analysis :"
    const anaLabel = sheet.getCell("A26");
    anaLabel.value = "03) Analysis :";
    anaLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("A26:C26");
    anaLabel.border = thinBorder;

    // D26 -> "06) Results :"
    const res2Label = sheet.getCell("D26");
    res2Label.value = "06) Results :";
    res2Label.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("D26:J26");
    res2Label.border = thinBorder;

    // K26 -> "10) Scope & Plan for Horizontal Deployment :"
    const deployLabel = sheet.getCell("K26");
    deployLabel.value = "10) Scope & Plan for Horizontal Deployment :";
    deployLabel.font = { bold: true, color: { argb: "FF0000FF" }, italic: true };
    sheet.mergeCells("K26:O26");
    deployLabel.border = thinBorder;


    // --- ROW 27-30: BOTTOM CONTENT ---
    // A27:C30 -> Analysis Content
    sheet.mergeCells("A27:C30");
    const anaContent = sheet.getCell("A27");
    anaContent.border = thinBorder;

    // D27:J30 -> Results Content (Maybe estimated savings here again?)
    sheet.mergeCells("D27:J30");
    const res2Content = sheet.getCell("D27");
    res2Content.value = `Savings: ${formData.estimatedSavings || ""}`;
    res2Content.alignment = { vertical: "top" };
    res2Content.border = thinBorder;

    // K27:O30 -> Horizontal Deployment Table
    // Row 27: Headers
    const hdHeaders = ["Sr. No.", "M/c No.", "Target Date", "Responsibility", "Status"];
    // 5 headers, K-O cols? No, K-O is 5 cols. Perfect.
    hdHeaders.forEach((txt, idx) => {
      const cell = sheet.getRow(27).getCell(11 + idx); // K=11
      cell.value = txt;
      cell.font = { bold: true, color: { argb: "FF0000FF" }, italic: true, size: 9 };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = thinBorder;
    });

    // Row 28-30: Empty Rows for HD
    for (let r = 28; r <= 30; r++) {
      for (let c = 11; c <= 15; c++) {
        const cell = sheet.getRow(r).getCell(c);
        cell.border = thinBorder;
      }
    }


    // --- IMAGES INSERTION ---
    // Helper to convert buffer to base64
    const arrayBufferToBase64 = (buffer) => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    const addImageToSheet = async (url, tl, br, errorCell) => {
      try {
        if (!url) return;

        // CONSTRUCT PROXY-FRIENDLY URL
        // If the URL matches our API_URL, we strip it to make it relative
        // This forces the request to go through the dev server proxy (localhost:3000 -> localhost:7289)
        // bypassing CORS issues.
        let fetchUrl = getImageUrl(url);
        if (fetchUrl.startsWith(API_URL)) {
          fetchUrl = fetchUrl.substring(API_URL.length);
          // Ensure it starts with / if not empty
          if (fetchUrl && !fetchUrl.startsWith('/')) fetchUrl = '/' + fetchUrl;
        }

        let base64Data;
        let extension = 'png';
        const token = authService.getToken();

        if (fetchUrl.startsWith('blob:') || fetchUrl.startsWith('data:')) {
          if (fetchUrl.startsWith('data:')) {
            base64Data = fetchUrl.split(',')[1];
            extension = fetchUrl.split(';')[0].split('/')[1] === 'jpeg' ? 'jpeg' : 'png';
          } else {
            const response = await fetch(fetchUrl);
            const blob = await response.ok ? await response.blob() : null;
            if (!blob) return;
            const buffer = await blob.arrayBuffer();
            base64Data = arrayBufferToBase64(buffer);
            extension = blob.type.split('/')[1] === 'jpeg' ? 'jpeg' : 'png';
          }
        } else {
          // PROXY REQUEST PATH
          // We use the relative fetchUrl here. 
          // The Auth header is still good to have, though cookie-based proxying might be handling it too 
          // (but we use Bearer tokens commonly).
          try {
            // Attempt 1: Relative URL with Axios (Auth Header)
            const response = await axios.get(fetchUrl, {
              responseType: 'arraybuffer',
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            base64Data = arrayBufferToBase64(response.data);
            const contentType = response.headers['content-type'];
            if (contentType && contentType.includes('jpeg')) extension = 'jpeg';
            else if (contentType && contentType.includes('gif')) extension = 'gif';
          } catch (authError) {
            console.warn(`Proxy fetch failed for ${fetchUrl}, trying absolute URL...`, authError.message);

            // Fallback: If relative fails (maybe proxy not set up for this path?), try absolute
            const absoluteUrl = getImageUrl(url); // Original full URL
            try {
              const response = await axios.get(absoluteUrl, {
                responseType: 'arraybuffer',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
              });
              base64Data = arrayBufferToBase64(response.data);
              const contentType = response.headers['content-type'];
              if (contentType && contentType.includes('jpeg')) extension = 'jpeg';
              else if (contentType && contentType.includes('gif')) extension = 'gif';
            } catch (absError) {
              // Fallback 3: Canvas (last resort)
              try {
                console.warn("Absolute fetch failed, trying Canvas...", absError.message);
                base64Data = await new Promise((resolve, reject) => {
                  const img = new Image();
                  img.crossOrigin = "Anonymous";
                  img.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL("image/png");
                    resolve(dataURL.split(",")[1]);
                  };
                  img.onerror = (e) => reject(new Error("Image load error"));
                  img.src = absoluteUrl;
                });
              } catch (canvasError) {
                // If all fail, report the original network error
                throw new Error(`Load Failed: ${authError.message}`);
              }
            }
          }
        }

        if (!base64Data) {
          if (errorCell) sheet.getCell(errorCell).value = "Image No Data";
          return;
        }

        const imageId = workbook.addImage({
          base64: base64Data,
          extension: extension,
        });

        sheet.addImage(imageId, {
          tl: tl,
          br: br,
          editAs: 'twoCell'
        });
      } catch (e) {
        console.error(`Failed to add image to Excel export (${url}):`, e.message);
        if (errorCell) {
          sheet.getCell(errorCell).value = `Err: ${e.message}`;
        }
      }
    };

    // Set row heights for rows 12-25 (Images)
    for (let r = 12; r <= 25; r++) {
      sheet.getRow(r).height = 20;
    }

    if (formData.problemStatementImage) {
      await addImageToSheet(formData.problemStatementImage,
        { col: 0.1, row: 11.2 }, // Start inside A12
        { col: 2.9, row: 24.8 }, // End inside C25
        "A12"
      );
    }

    if (formData.proposedIdeaImage) {
      await addImageToSheet(formData.proposedIdeaImage,
        { col: 3.1, row: 11.2 }, // Start inside D12
        { col: 9.9, row: 24.8 }, // End inside J25
        "D12"
      );
    }

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Kaizen_Idea_${formData.referenceNumber || id || "New"}.xlsx`);
  };

  const loadInitialData = async () => {
    try {
      if (!sessionStorage.getItem("kaizen_categories")) {
        setLoading(true);
      }
      setError("");

      const [
        plantsData,
        categoriesData,
        machinesData,
        productsData,
        statusesData,
        usersData,
      ] = await Promise.all([
        plantService.getAllPlants(),
        masterDataService.getCategories?.(tenantId) || Promise.resolve([]),
        machineService.getAllMachines(),
        productService.getAllProducts(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
        userService.getAllUsers(),
      ]);

      setPlants(plantsData);

      // Auto-select plant if only 1 exists (for new entries)
      if (!isEditMode && !formData.plantId && plantsData?.length === 1) {
        setFormData(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      // Filter categories for Kaizen type
      const kaizenCategories = categoriesData.filter(
        (c) => c.categoryType === "Kaizen",
      );
      setCategories(kaizenCategories);

      setMachines(machinesData);
      setProducts(productsData);
      setUsers(usersData); // Store all users

      // Filter statuses for Kaizen type
      const kaizenStatuses = statusesData.filter(
        (s) => s.statusType === "Kaizen",
      );
      setStatuses(kaizenStatuses);

      // Cache the filtered data
      sessionStorage.setItem(
        "kaizen_categories",
        JSON.stringify(kaizenCategories),
      );
      sessionStorage.setItem("machines_data", JSON.stringify(machinesData));
      sessionStorage.setItem("products_data", JSON.stringify(productsData));
      sessionStorage.setItem("kaizen_statuses", JSON.stringify(kaizenStatuses));

      // If editing, load the idea
      if (isEditMode) {
        await loadIdeaData();
      }
    } catch (err) {
      setError("Failed to load initial data. Please try again.");
      console.error("Error loading initial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadIdeaData = async () => {
    try {
      const idea = await kaizenService.getKaizenIdeaById(id);
      setFormData({
        title: idea.title || "",
        problemStatement: idea.problemStatement || "",
        problemStatementImage: idea.problemStatementImage || "",
        proposedIdeaImage: idea.proposedIdeaImage || "",
        currentState: idea.currentState || "",
        proposedIdea: idea.proposedIdea || "",
        plantId: idea.plantId?.toString() || "",
        categoryId: idea.categoryId?.toString() || "",
        estimatedSavings: idea.estimatedSavings?.toString() || "",
        machineId: idea.machineId?.toString() || "",
        productId: idea.productId?.toString() || "",
        statusId: idea.statusId?.toString() || "",
        referenceNumber: idea.referenceNumber || null,
        moduleName: idea.moduleName || "",
        benchmark: idea.benchmark || "",
        kaizenStartDate: idea.kaizenStartDate
          ? idea.kaizenStartDate.split("T")[0]
          : "",
        kaizenFinishDate: idea.kaizenFinishDate
          ? idea.kaizenFinishDate.split("T")[0]
          : "",
        teamMembers: idea.teamMembers ? idea.teamMembers.split(",") : [],
        kaizenCost: idea.kaizenCost?.toString() || "",
        benefits: idea.benefits || "",
        resultArea: idea.resultArea ? idea.resultArea.split(",") : [],
        activity: idea.activity || "",
        lossNumber: idea.lossNumber || "",
      });
    } catch (err) {
      setError("Failed to load Kaizen idea. Please try again.");
      console.error("Error loading idea:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTeamMembersChange = (userName) => {
    setFormData((prev) => {
      const currentMembers = prev.teamMembers || [];
      if (currentMembers.includes(userName)) {
        return {
          ...prev,
          teamMembers: currentMembers.filter((m) => m !== userName),
        };
      } else {
        return { ...prev, teamMembers: [...currentMembers, userName] };
      }
    });
  };

  const handleResultAreaChange = (area) => {
    setFormData((prev) => {
      const currentAreas = prev.resultArea || [];
      if (currentAreas.includes(area)) {
        return {
          ...prev,
          resultArea: currentAreas.filter((a) => a !== area),
        };
      } else {
        return { ...prev, resultArea: [...currentAreas, area] };
      }
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".multi-select-container")) {
        setIsTeamDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleImageUpload = async (e, fieldName = "problemStatementImage") => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const data = await uploadService.uploadFile(file);
      setFormData((prev) => ({
        ...prev,
        [fieldName]: data.url,
      }));
    } catch (err) {
      console.error("Upload failed", err);
      // alert("Failed to upload image");
      setError("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title?.trim()) {
      errors.title = "Title is required";
    }
    if (!formData.problemStatement?.trim()) {
      errors.problemStatement = "Problem statement is required";
    }
    if (
      formData.estimatedSavings &&
      isNaN(parseFloat(formData.estimatedSavings))
    ) {
      errors.estimatedSavings = "Estimated savings must be a valid number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (isEditMode && !canEdit) {
      setError("You do not have permission to edit Kaizen ideas");
      return;
    }
    if (!isEditMode && !canCreate) {
      setError("You do not have permission to create Kaizen ideas");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const ideaData = {
        title: formData.title.trim(),
        problemStatement: formData.problemStatement.trim(),
        currentState: formData.currentState?.trim() || null,
        proposedIdea: formData.proposedIdea?.trim() || null,
        plantId: formData.plantId ? parseInt(formData.plantId) : null,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        estimatedSavings: formData.estimatedSavings
          ? parseFloat(formData.estimatedSavings)
          : null,
        machineId: formData.machineId ? parseInt(formData.machineId) : null,
        productId: formData.productId ? parseInt(formData.productId) : null,
        submittedBy: currentUser?.userId || null,
        statusId: formData.statusId ? parseInt(formData.statusId) : null,
        referenceNumber: formData.referenceNumber,
        moduleName: formData.moduleName,
        problemStatementImage: formData.problemStatementImage,
        proposedIdeaImage: formData.proposedIdeaImage,
        benchmark: formData.benchmark,
        kaizenStartDate: formData.kaizenStartDate || null,
        kaizenFinishDate: formData.kaizenFinishDate || null,
        teamMembers: formData.teamMembers.join(","),
        kaizenCost: formData.kaizenCost
          ? parseFloat(formData.kaizenCost)
          : null,
        benefits: formData.benefits,
        resultArea: formData.resultArea.join(","),
        activity: formData.activity,
        lossNumber: formData.lossNumber,
      };

      if (isEditMode) {
        await kaizenService.updateKaizenIdea(id, ideaData);
        setSuccess("Kaizen idea updated successfully!");
      } else {
        await kaizenService.createKaizenIdea(ideaData);
        setSuccess("Kaizen idea created successfully!");
      }

      setTimeout(() => {
        navigate("/home/kaizen");
      }, 1500);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        `Failed to ${isEditMode ? "update" : "create"} Kaizen idea. Please try again.`;
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer">
        <PageLoading message="Loading Kaizen…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer kaizen-entry-page">
      <div className="page-header">
        <div className="header-content">
          <h1>{isEditMode ? "Edit Kaizen Idea" : "New Kaizen Idea"}</h1>
          <p>
            {isEditMode
              ? "Update your continuous improvement idea"
              : "Submit a new continuous improvement idea"}
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* --- PRINT VIEW START --- */}
      <div className="print-only-content">
        <div className="kaizen-sheet">
          {/* Header Section */}
          <div className="sheet-header">
            <div className="logo-section">
              <div className="tpm-logo">TPM CLUB INDIA</div>
            </div>
            <div className="title-section">
              <h2>KAIZEN IDEA - SHEET</h2>
            </div>
            <div className="meta-table-section kizenresultarea">
              <table className="meta-table">
                <tbody>
                  <tr>
                    <td className="meta-label">Activity</td>
                    <td
                      className={formData.activity === "KK" ? "selected" : ""}
                    >
                      KK
                    </td>
                    <td
                      className={formData.activity === "JH" ? "selected" : ""}
                    >
                      JH
                    </td>
                    <td
                      className={formData.activity === "QM" ? "selected" : ""}
                    >
                      QM
                    </td>
                    <td
                      className={formData.activity === "PM" ? "selected" : ""}
                    >
                      PM
                    </td>
                    <td
                      className={formData.activity === "SHE" ? "selected" : ""}
                    >
                      SHE
                    </td>
                    <td
                      className={formData.activity === "OTPM" ? "selected" : ""}
                    >
                      OTPM
                    </td>
                    <td
                      className={formData.activity === "DM" ? "selected" : ""}
                    >
                      DM
                    </td>
                    <td
                      className={formData.activity === "ET" ? "selected" : ""}
                    >
                      ET
                    </td>
                  </tr>
                  <tr>
                    <td className="meta-label">Loss No./Step</td>
                    <td colSpan="8">{formData.lossNumber || ""}</td>
                  </tr>
                  <tr>
                    <td className="meta-label">Result Area</td>
                    <td
                      className={
                        (formData.resultArea || []).includes("P")
                          ? "selected"
                          : ""
                      }
                    >
                      P
                    </td>
                    <td
                      className={
                        (formData.resultArea || []).includes("Q")
                          ? "selected"
                          : ""
                      }
                    >
                      Q
                    </td>
                    <td
                      className={
                        (formData.resultArea || []).includes("C")
                          ? "selected"
                          : ""
                      }
                    >
                      C
                    </td>
                    <td
                      className={
                        (formData.resultArea || []).includes("D")
                          ? "selected"
                          : ""
                      }
                    >
                      D
                    </td>
                    <td
                      className={
                        (formData.resultArea || []).includes("S")
                          ? "selected"
                          : ""
                      }
                    >
                      S
                    </td>
                    <td
                      className={
                        (formData.resultArea || []).includes("M")
                          ? "selected"
                          : ""
                      }
                    >
                      M
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="sub-header-row">
            <div className="info-box">
              <span className="label">Plant:</span>
              <span className="value">
                {plants.find((p) => p.plantId == formData.plantId)?.plantName ||
                  formData.plantId}
              </span>
            </div>
            <div className="info-box">
              <span className="label">Machine:</span>
              <span className="value">
                {machines.find((m) => m.machineId == formData.machineId)
                  ?.machineName || formData.machineId}
              </span>
            </div>
            <div className="info-box">
              <span className="label">Sr. No:</span>
              <span className="value">{formData.referenceNumber || ""}</span>
            </div>
            <div className="info-box">
              <span className="label">Prepared By:</span>
              <span className="value">{currentUser?.username || ""}</span>
            </div>
          </div>

          {/* Main Grid Content */}
          <div className="sheet-body">
            {/* Row 1: Theme */}
            <div className="sheet-row theme-row">
              <div className="section-label">1. Kaizen theme :</div>
              <div className="section-content">{formData.title}</div>
            </div>

            <div className="sheet-grid-layout">
              {/* Left Column */}
              <div className="left-col">
                <div className="box-section">
                  <div className="box-title">
                    2. Problem / Present status : (With Sketch)
                  </div>
                  <div className="box-content">
                    <p>{formData.problemStatement}</p>
                    {formData.problemStatementImage && (
                      <div className="print-image-container">
                        <img
                          src={getImageUrl(formData.problemStatementImage)}
                          alt="Problem"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Middle Column */}
              <div className="middle-col">
                <div className="box-section">
                  <div className="box-title">3. Idea :</div>
                  <div className="box-content small-text">
                    {formData.proposedIdea}
                  </div>
                </div>

                <div className="box-section">
                  <div className="box-title">
                    4. Countermeasure : (With Sketch)
                  </div>
                  <div className="box-content">
                    <p>{formData.proposedIdea}</p>
                    {formData.proposedIdeaImage && (
                      <div className="print-image-container">
                        <img
                          src={getImageUrl(formData.proposedIdeaImage)}
                          alt="proposed"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="box-section">
                  <div className="box-title">5. Result :</div>
                  <div className="box-content small-text">
                    <p>{formData.benefits}</p>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="right-col">
                <table className="right-info-table">
                  <tbody>
                    <tr>
                      <td className="label-cell">Bench Mark</td>
                      <td className="value-cell">{formData.benchmark}</td>
                    </tr>
                    <tr>
                      <td className="label-cell">Target</td>
                      <td className="value-cell"></td>
                    </tr>
                    <tr>
                      <td className="label-cell">Kaizen start</td>
                      <td className="value-cell">{formData.kaizenStartDate}</td>
                    </tr>
                    <tr>
                      <td className="label-cell">Kaizen Finish</td>
                      <td className="value-cell">
                        {formData.kaizenFinishDate}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="team-section">
                  <div className="box-title">Team Members</div>
                  <div className="box-content small-text">
                    {(formData.teamMembers || []).join(", ")}
                  </div>
                </div>

                <div className="box-section">
                  <div className="box-title">6. Benefits :</div>
                  <div className="box-content small-text">
                    Savings: {formData.estimatedSavings}
                  </div>
                </div>

                <div className="box-section">
                  <div className="box-title">7. Cost of KAIZEN</div>
                  <div className="box-content small-text">
                    {formData.kaizenCost}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* --- PRINT VIEW END --- */}

      <div>
        <form onSubmit={handleSubmit} className="kaizen-entry-form">
          {/* Basic Information */}
          <div className="form-section wd70">
            <h3 className="section-title fnt20">Basic Information</h3>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  maxLength={150}
                  className={formErrors.title ? "error" : ""}
                  placeholder="Enter a brief title for your Kaizen idea"
                />
                {formErrors.title && (
                  <span className="field-error">{formErrors.title}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="problemStatement">
                  Problem Statement <span className="required">*</span>
                </label>
                <textarea
                  id="problemStatement"
                  name="problemStatement"
                  value={formData.problemStatement}
                  onChange={handleInputChange}
                  rows={4}
                  className={formErrors.problemStatement ? "error" : ""}
                  placeholder="Describe the problem or opportunity you've identified..."
                />
                <div style={{ marginTop: "20px" }}>
                  <label style={{ display: "block", marginBottom: "5px" }}>
                    Sketch / Image (Optional)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                    {uploading && <span>Uploading...</span>}
                  </div>
                </div>
                {formData.problemStatementImage && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={getImageUrl(formData.problemStatementImage)}
                      alt="Problem Sketch"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
                {formErrors.problemStatement && (
                  <span className="field-error">
                    {formErrors.problemStatement}
                  </span>
                )}
              </div>
            </div>

            <div className="row">
              <div class="col-md-6">
                <div>
                  <div className="form-group full-width">
                    <label htmlFor="currentState">Current State</label>
                    <textarea
                      id="currentState"
                      name="currentState"
                      value={formData.currentState}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Describe the current state or process..."
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div >
                  <div className="form-group full-width">
                    <label htmlFor="proposedIdea">
                      Countermeasure / Proposed Idea
                    </label>
                    <textarea
                      id="proposedIdea"
                      name="proposedIdea"
                      value={formData.proposedIdea}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Describe your countermeasure or proposed solution..."
                    />

                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="form-group" style={{ marginTop: "15px" }}>
                  <label style={{ display: "block", marginBottom: "5px" }}>
                    Countermeasure Sketch / Image (Optional)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageUpload(e, "proposedIdeaImage")
                      }
                      disabled={uploading}
                    />
                    {uploading && <span>Uploading...</span>}
                  </div>
                </div>
                {formData.proposedIdeaImage && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={getImageUrl(formData.proposedIdeaImage)}
                      alt="Countermeasure Sketch"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>





            {/* Results & Implementation Component */}
            <KaizenResults
              formData={formData}
              handleInputChange={handleInputChange}
              handleTeamMembersChange={handleTeamMembersChange}
              users={users}
              isTeamDropdownOpen={isTeamDropdownOpen}
              setIsTeamDropdownOpen={setIsTeamDropdownOpen}
              handleResultAreaChange={handleResultAreaChange}
              setFormData={setFormData}
            />
          </div>

          {/* Classification */}
          <div className="form-section wd30 classification-section">
            <h3 className="section-title fnt20">Classification</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="plantId">Plant</label>
                <select
                  id="plantId"
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Plant (Optional)</option>
                  {plants.map((plant) => (
                    <option key={plant.plantId} value={plant.plantId}>
                      {plant.plantName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="categoryId">Category</label>
                <select
                  id="categoryId"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category (Optional)</option>
                  {categories.map((category) => (
                    <option
                      key={category.categoryId}
                      value={category.categoryId}
                    >
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="machineId">Machine</label>
                <select
                  id="machineId"
                  name="machineId"
                  value={formData.machineId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Machine (Optional)</option>
                  {machines.map((machine) => (
                    <option key={machine.machineId} value={machine.machineId}>
                      {machine.machineName} ({machine.machineCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="productId">Product</label>
                <select
                  id="productId"
                  name="productId"
                  value={formData.productId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Product (Optional)</option>
                  {products.map((product) => (
                    <option key={product.productId} value={product.productId}>
                      {product.productName} ({product.productCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estimatedSavings">Estimated Savings (₹)</label>
                <input
                  type="number"
                  id="estimatedSavings"
                  name="estimatedSavings"
                  value={formData.estimatedSavings}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className={formErrors.estimatedSavings ? "error" : ""}
                  placeholder="0.00"
                />
                {formErrors.estimatedSavings && (
                  <span className="field-error">
                    {formErrors.estimatedSavings}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="statusId">Status</label>
                <select
                  id="statusId"
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status (Optional)</option>
                  {statuses.map((status) => (
                    <option key={status.statusId} value={status.statusId}>
                      {status.statusName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => window.print()}
            >
              <HiOutlinePrinter />
              Print
            </button>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={handleExportExcel}
            >
              <HiOutlineTableCells />
              Export Excel
            </button>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => navigate("/home/kaizen")}
            >
              <HiOutlineXMark />
              Cancel
            </button>
            <button type="submit" className="btn-primary btn" disabled={submitting}>
              {submitting ? (
                "Submitting..."
              ) : (
                <>
                  <HiOutlineCheckCircle />
                  {isEditMode ? "Update Idea" : "Submit Idea"}
                </>
              )}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default KaizenEntry;
